import firestoreRedux from '@dreamworld/firestore-redux';
import { requestApi } from '../request-api.js';

/**
 * The Translation library holds one Translator and one schema for the whole app, and
 * `setSchema` replaces the previous schema wholesale - so the features that translate content
 * (Impersonate > Translate, and Translate a Card) cannot each configure their own. This module is
 * the one place that does it, called once at app start; a feature then only picks a language and
 * starts its own activation.
 *
 * The field-level helpers are shared, since a card collection and a board collection often have
 * the same document shape.
 */

/**
 * How many array elements the bracket-indexed skips below cover, per field.
 *
 * A field path in the schema matches exactly, and there is no array wildcard - `toolCalls.name`
 * does not match `toolCalls[0].name`. Each array is capped generously above its realistic size;
 * an element beyond the cap would not be skipped, so these are picked to comfortably exceed
 * anything the product actually produces today.
 */
const MAX_TOOL_CALLS = 30;
const MAX_ASSIGNEES = 20;
const MAX_REFERENCES = 10;
const MAX_RESPONSES = 10;

/**
 * Skips `name`, `input`, and `output` on every tool call.
 *
 * `name` is an identifier, not prose: it is looked up against the tools config
 * (`toolsConf[tool.name]`), compared against literals like `'createNewBoard'`, and used to build
 * keys. `input`/`output` vary per tool (e.g. `webFetch.input.url`, `webSearch.input.query`,
 * `webFetch.output.{url,content}`, `webSearch.output.result`) - skipping both wholesale, rather
 * than allowlisting one field at a time per tool, covers every tool's shape without needing to
 * track each one individually.
 */
const toolCallSkips = () => {
  const skips = {};
  for (let i = 0; i < MAX_TOOL_CALLS; i++) {
    skips[`toolCalls[${i}].name`] = { skip: true };
    skips[`toolCalls[${i}].input`] = { skip: true };
    skips[`toolCalls[${i}].output`] = { skip: true };
  }
  return skips;
};

/** Skips every element of a card's `assignments` array - bare user-id strings, not prose. */
const cardAssignmentSkips = () => {
  const skips = {};
  for (let i = 0; i < MAX_ASSIGNEES; i++) {
    skips[`assignments[${i}]`] = { skip: true };
  }
  return skips;
};

/** Skips the `id` of every attachment reference and every response on an AI message. */
const aiMessageIndexedSkips = () => {
  const skips = {};
  for (let i = 0; i < MAX_REFERENCES; i++) {
    skips[`request.references[${i}].id`] = { skip: true };
  }
  for (let i = 0; i < MAX_RESPONSES; i++) {
    skips[`responses[${i}].id`] = { skip: true };
  }
  return skips;
};

/** Field-level skips shared by `board-chat-messages` and `card-chat-messages` - same doc shape. */
const chatMessageSkips = () => ({
  // Array of mentioned user ids (from `@usr_` mentions) - corrupting these breaks mention tracking.
  referencedUsers: { skip: true },
  // Its tags (formats and reference anchors) must come back intact. An old message without it shows `message`.
  messageHtml: { contentType: 'HTML' },
  // Nothing shows it any more.
  htmlMessage: { skip: true },
});

/** Field-level skips shared by `board-attachments` and `card-attachments` - same doc shape. */
const attachmentSkips = () => ({
  url: { skip: true },
  contentType: { skip: true }, // MIME string, e.g. 'text/html' - not enum-shaped, auto-skip misses it.
});

/**
 * Translator for the Translation library, in its function form: `POST /ai/translate` through
 * `requestApi`, so the call carries the app's standard auth and headers. The endpoint's request and
 * response already match the library's contract, so nothing needs re-shaping here.
 * @param {Object} param0
 *  @property {String} targetLanguage Language to translate into.
 *  @property {Object} items Keyed by the library's opaque wire id. e.g. `{ $id: { text, contentType } }`
 * @returns {Promise} Resolves with `{ targetLanguage, items }`, each item `{ text, success, error }`.
 */
const translateViaAiServer = ({ targetLanguage, items }) =>
  // `silent`: a failed item already ends FAILED, and Translate a Card shows its own toast for it.
  requestApi('/ai/translate', { method: 'POST', body: { targetLanguage, items }, silent: true });

/**
 * Identity fields, wherever they appear. These name a record, never content - translating one
 * corrupts the lookup it keys. Declared once here instead of repeated per collection: a
 * collection's own entry is merged *over* this base, field by field, so declaring something
 * specific never drops what's declared here. (The document level behaves the opposite way - a
 * document's own entry replaces its collection's `'*'` wholesale.) A collection that genuinely
 * needs one of these translated can say so with `{ skip: false }`.
 * The document's own root `id` needs no entry at all - the library skips it by default.
 */
const IDENTITY_SKIPS = {
  '*': {
    '*': {
      accountId: { skip: true },
      boardId: { skip: true },
      cardId: { skip: true },
      chatId: { skip: true },
      userId: { skip: true },
      ownerId: { skip: true },
      createdBy: { skip: true },
      updatedBy: { skip: true },
      statusChangedBy: { skip: true },
    },
  },
};

/**
 * The one schema the whole app translates by. The card's own documents (`cards` through
 * `history-items`) are what Translate a Card's activation admits; everything after them only the
 * impersonated session's wide activation ever reaches.
 */
const TRANSLATION_SCHEMA = {
  ...IDENTITY_SKIPS,
  cards: {
    '*': {
      // An old card's description (no `descriptionHtml`): plain text, or Squire HTML on very old cards.
      // Its contentType is left undeclared, as for a chat's `message`, so the Translator detects which.
      descriptionHtml: { contentType: 'HTML' },
      description2: { skip: true }, // Nothing shows it any more.
      columnId: { skip: true },
      prevColumnId: { skip: true },
      lastMovedBy: { skip: true },
      ...cardAssignmentSkips(),
    },
  },
  tasks: {
    '*': { closedBy: { skip: true } },
  },
  'card-attachments': { '*': attachmentSkips() },
  'card-chat-messages': { '*': chatMessageSkips() },
  'history-items': {
    // `detail.*`'s shape is action-dependent (assignee ids for one action, tag snapshots for
    // another, etc.) and isn't uniformly skippable - only the always-present reference fields,
    // plus per-action id fields confirmed live (`detail.columnId` on a column-move entry), are.
    '*': { cardChildId: { skip: true }, 'detail.columnId': { skip: true } },
  },
  accounts: {
    '*': {
      stripeCustomerId: { skip: true },
      coupon: { skip: true },
      enterpriseMonthlyPriceId: { skip: true },
      enterpriseYearlyPriceId: { skip: true },
      currency: { skip: true }, // 3-letter lowercase ISO code, e.g. 'usd' - not ALL_CAPS-shaped.
      features: { skip: true }, // Kerika-specific tokens, not prose.
    },
  },
  users: {
    '*': {
      email: { skip: true },
      auths: { skip: true },
      lastLoginIp: { skip: true },
      primaryAuthId: { skip: true },
      profileImage: { skip: true },
      timezone: { skip: true },
      joinSecret: { skip: true }, // a genuine secret/token string.
    },
  },
  boards: {
    '*': {
      // A board's template category is an identifier, not prose: Home groups, collapses and
      // sorts by its raw value. The library's automatic enum-skip doesn't catch it, because the
      // value isn't `ALL_CAPS_WITH_UNDERSCORES`-shaped - hence this explicit skip. Skipped on
      // both `boards` and `user-accessible-boards` below, since Home resolves the value from
      // either one depending on what's loaded.
      templateCategory: { skip: true },
      canvasId: { skip: true },
      baseBoardId: { skip: true },
      lang: { skip: true }, // a language code - same corruption risk as `system-language`.
    },
  },
  'user-accessible-boards': {
    '*': { templateCategory: { skip: true } },
  },
  'board-tags': {
    '*': { color: { skip: true } }, // a `TagColors.Color` id reference, not the literal color.
  },
  'board-attachments': { '*': attachmentSkips() },
  'active-timers': {
    // `description` (the user's own free-text note) is left translatable. `tags` is skipped
    // rather than translated: it's selected from the connected provider's (e.g. Kimai) own tag
    // list, and the value round-trips back to that provider on submit - translating it risks a
    // mismatch, unlike `board-tags.name` which never leaves this app.
    '*': { tags: { skip: true } },
  },
  'ai-messages': {
    '*': {
      'request.quickAction.id': { skip: true },
      'request.quickAction.icon': { skip: true },
      // `request.quickAction.title` is left translatable: it's a copy of a static
      // `ai-quick-actions` template title, but it's rendered directly in the chat as what the
      // salesman clicked - leaving it untranslated would show one English label sitting inside an
      // otherwise-translated conversation.
      ...aiMessageIndexedSkips(),
    },
  },
  'board-chat-messages': { '*': chatMessageSkips() },
  'ai-chat-attachments': {
    '*': {
      contentType: { skip: true }, // MIME string.
      // Misleadingly named - it's actually the uploaded file's filename (`file.name`), not a
      // display title. Translating a filename is actively wrong.
      title: { skip: true },
    },
  },
  'ai-tool-messages': {
    '*': toolCallSkips(),
  },
};

/**
 * Configures the Translation library for the whole app - the Translator it calls out to, and the
 * schema every activation translates by. Called once at app start, before any feature starts an
 * activation of its own.
 */
export function configureTranslation() {
  firestoreRedux.translation.setTranslator(translateViaAiServer);
  firestoreRedux.translation.setSchema(TRANSLATION_SCHEMA);
}
